import { useMemo } from "react";
import type { SWRConfiguration, SWRResponse } from "swr";

import { type ResponseStatus, type IModelMetadataResponse, type IReturn } from "src/api/Tmd.dtos";
import useApi from "src/hooks/api/useApi";

/**
 * Defines the response payload and status expected by this shared request adapter.
 */
export type TMDLoadByIdResponse<TModel> = {
    responseStatus: ResponseStatus;
    model: TModel;
};

/**
 * Defines the typed request payload constructed by this shared adapter.
 */
export type TMDLoadByIdRequest<
    TModel,
    TResponse extends TMDLoadByIdResponse<TModel>
> = IReturn<TResponse> & {
    id?: number;
};

/**
 * Describes projection of a response into its model value.
 */
type GetDataFn<TModel, TResponse extends TMDLoadByIdResponse<TModel>> = (
    response?: SWRResponse<TResponse, TResponse>
) => TModel;

const getData = <TModel, TResponse extends TMDLoadByIdResponse<TModel>>(
    response?: SWRResponse<TResponse, TResponse>
) => response?.data?.model;
const getError = <TModel, TResponse extends TMDLoadByIdResponse<TModel>>(
    response?: SWRResponse<TResponse, TResponse>
) => (response?.error ?? response?.data)?.responseStatus;
const getAdditionalState = <TModel, TResponse extends TMDLoadByIdResponse<TModel>>(
    _response?: SWRResponse<TResponse, TResponse>
) => ({});

/**
 * Loads one model through an ID-bearing GET request.
 *
 * Postconditions: Falsy constructor or ID disables the request, including ID zero. Negative IDs
 * are not rejected here. Returned state uses the shared model/status projection.
 */
const useBaseLoadById = <
    TModel,
    TResponse extends TMDLoadByIdResponse<TModel>,
    TRequest extends TMDLoadByIdRequest<TModel, TResponse>
>(
    constructor: new (init?: Partial<TRequest>) => TRequest,
    id?: number | null | undefined,
    configuration?: SWRConfiguration<TResponse, TResponse> | null | undefined
) => {
    const getRequest = useMemo(
        () => {
            if (!constructor || !id) {
                return undefined;
            }

            return (): IReturn<TResponse> =>
                new constructor({
                    id
                } as Partial<TRequest>);
        },
        [constructor, id]
    );

    return useApi(
        "get",
        getRequest,
        { getData: getData as GetDataFn<TModel, TResponse>, getError, getAdditionalState },
        configuration
    );
};

export default useBaseLoadById;
